import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Scale,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { FinancialIntegrationService } from '@/services/financialIntegrationService';
import { FinancialCaseIntegration } from './FinancialCaseIntegration';

interface ProcessFinancialSummary {
  caseId: string;
  title: string;
  caseNumber?: string;
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  pendingAmount: number;
  paidAmount: number;
  overdueAmount: number;
  status: string;
  phase: string;
}

export const ProcessesFinancialDashboard: React.FC = () => {
  const [processesSummary, setProcessesSummary] = useState<ProcessFinancialSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadProcessesFinancialData();
  }, []);

  const loadProcessesFinancialData = async () => {
    try {
      setLoading(true);
      
      // Buscar todos os processos ativos
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: cases, error } = await supabase
        .from('cases')
        .select('id, title, case_number, status, phase, archived')
        .eq('user_id', user.id)
        .eq('archived', false)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Para cada processo, buscar o resumo financeiro
      const summaries: ProcessFinancialSummary[] = [];
      
      for (const case_ of cases || []) {
        try {
          const financialSummary = await FinancialIntegrationService.getProcessFinancialSummary(case_.id);
          
          summaries.push({
            caseId: case_.id,
            title: case_.title,
            caseNumber: case_.case_number,
            ...financialSummary,
            status: case_.status,
            phase: case_.phase
          });
        } catch (error) {
          console.error(`Erro ao carregar dados financeiros do processo ${case_.id}:`, error);
          // Adicionar processo sem dados financeiros
          summaries.push({
            caseId: case_.id,
            title: case_.title,
            caseNumber: case_.case_number,
            totalRevenue: 0,
            totalExpenses: 0,
            netProfit: 0,
            pendingAmount: 0,
            paidAmount: 0,
            overdueAmount: 0,
            status: case_.status,
            phase: case_.phase
          });
        }
      }

      setProcessesSummary(summaries);
    } catch (error) {
      console.error('Erro ao carregar dados financeiros dos processos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Em andamento':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Concluído':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Suspenso':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Arquivado':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPhaseColor = (phase: string) => {
    switch (phase?.toLowerCase()) {
      case 'petição inicial':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'audiência':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'sentença':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'recurso':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            Dashboard Financeiro dos Processos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calcular totais gerais
  const totalRevenue = processesSummary.reduce((sum, p) => sum + p.totalRevenue, 0);
  const totalExpenses = processesSummary.reduce((sum, p) => sum + p.totalExpenses, 0);
  const totalNetProfit = processesSummary.reduce((sum, p) => sum + p.netProfit, 0);
  const totalPending = processesSummary.reduce((sum, p) => sum + p.pendingAmount, 0);
  const totalPaid = processesSummary.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalOverdue = processesSummary.reduce((sum, p) => sum + p.overdueAmount, 0);

  // Processos com valores pendentes
  const processesWithPending = processesSummary.filter(p => p.pendingAmount > 0);
  const processesWithOverdue = processesSummary.filter(p => p.overdueAmount > 0);
  const receivedShare = totalRevenue > 0 ? (totalPaid / totalRevenue) * 100 : 0;
  const pendingShare = totalRevenue > 0 ? (totalPending / totalRevenue) * 100 : 0;
  const overdueShare = totalRevenue > 0 ? (totalOverdue / totalRevenue) * 100 : 0;
  const detailed = processesSummary
    .filter(p => p.totalRevenue > 0 || p.totalExpenses > 0)
    .sort((a, b) => b.netProfit - a.netProfit);

  return (
    <div className="space-y-5">
      {/* Painel financeiro dos processos */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="relative flex flex-col gap-4 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] px-5 py-6 sm:flex-row sm:items-end sm:justify-between sm:px-6">
          <img
            src="/imgs/logos allana/letra_A_logo.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-3 top-1/2 h-32 w-32 -translate-y-1/2 select-none object-contain opacity-[0.12]"
          />
          <div className="relative flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#c4ab83]/20 text-[#9a7f52]">
              <Scale className="h-6 w-6" />
            </span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Processos</p>
              <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Quanto os processos rendem</p>
              <p className="mt-1 text-sm text-slate-500">Honorários e despesas do escritório ligados a cada processo.</p>
            </div>
          </div>
          <div className="relative flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              className="h-9 rounded-xl border-slate-200 bg-white text-[13px] text-slate-700 shadow-none hover:bg-slate-50"
            >
              <Eye className="mr-1.5 h-4 w-4" />
              {showDetails ? 'Ocultar processos' : 'Ver por processo'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadProcessesFinancialData}
              className="h-9 rounded-xl text-[13px] text-slate-500 hover:text-slate-900"
            >
              Atualizar
            </Button>
          </div>
        </div>

        {/* Totais */}
        <div className="grid grid-cols-2 gap-px bg-slate-100 sm:grid-cols-4">
          {[
            { label: 'Honorários', value: totalRevenue, className: totalRevenue ? 'text-emerald-600' : 'text-slate-300', hint: 'total contratado' },
            { label: 'Despesas', value: totalExpenses, className: totalExpenses ? 'text-red-600' : 'text-slate-300', hint: 'do escritório' },
            { label: 'Resultado', value: totalNetProfit, className: totalNetProfit > 0 ? 'text-emerald-600' : totalNetProfit < 0 ? 'text-red-600' : 'text-slate-300', hint: 'honorários − despesas' },
            { label: 'A receber', value: totalPending, className: totalPending ? 'text-amber-600' : 'text-slate-300', hint: `${processesWithPending.length} ${processesWithPending.length === 1 ? 'processo' : 'processos'}` },
          ].map((tile) => (
            <div key={tile.label} className="bg-white px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{tile.label}</p>
              <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${tile.className}`}>{formatCurrency(tile.value)}</p>
              <p className="mt-0.5 text-[11px] text-slate-400">{tile.hint}</p>
            </div>
          ))}
        </div>

        {/* Recebimento dos honorários */}
        <div className="border-t border-slate-100 px-5 py-4 sm:px-6">
          <div className="mb-2 flex items-end justify-between text-xs">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Recebimento dos honorários</span>
            <span className="tabular-nums text-slate-500">
              {formatCurrency(totalPaid)} de {formatCurrency(totalRevenue)}
            </span>
          </div>
          <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100">
            <div className="bg-emerald-500 transition-all duration-700" style={{ width: `${receivedShare}%` }} />
            <div className="bg-amber-400 transition-all duration-700" style={{ width: `${pendingShare}%` }} />
            <div className="bg-red-400 transition-all duration-700" style={{ width: `${overdueShare}%` }} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Recebido {receivedShare.toFixed(0)}%</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-400" /> Pendente {pendingShare.toFixed(0)}%</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-400" /> Vencido {overdueShare.toFixed(0)}%</span>
          </div>
          {processesWithOverdue.length > 0 && (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
              <AlertTriangle className="h-3.5 w-3.5" />
              {processesWithOverdue.length} {processesWithOverdue.length === 1 ? 'processo com pagamento vencido' : 'processos com pagamentos vencidos'} · {formatCurrency(totalOverdue)}
            </p>
          )}
        </div>

        {/* Por processo */}
        {showDetails && (
          <div className="border-t border-slate-100">
            {detailed.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-slate-400">Nenhum processo com valores lançados.</p>
            ) : (
              <div className="max-h-[480px] divide-y divide-slate-100 overflow-y-auto">
                {detailed.map((process) => {
                  const share = process.totalRevenue > 0 ? (process.paidAmount / process.totalRevenue) * 100 : 0;
                  return (
                    <div key={process.caseId} className="flex flex-col gap-3 px-5 py-3.5 sm:flex-row sm:items-center sm:px-6">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900" title={process.title}>{process.title}</p>
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="h-1.5 w-32 overflow-hidden rounded-full bg-slate-100">
                            <div className="h-full bg-emerald-500" style={{ width: `${share}%` }} />
                          </div>
                          <span className="text-[11px] text-slate-400">{share.toFixed(0)}% recebido</span>
                          {process.status && <span className="text-[11px] text-slate-400">· {process.status}</span>}
                        </div>
                      </div>
                      <div className="grid shrink-0 grid-cols-3 gap-4 text-right text-xs">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Honorários</p>
                          <p className="font-semibold tabular-nums text-emerald-600">{formatCurrency(process.totalRevenue)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">A receber</p>
                          <p className={`font-semibold tabular-nums ${process.pendingAmount ? 'text-amber-600' : 'text-slate-300'}`}>{formatCurrency(process.pendingAmount)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Resultado</p>
                          <p className={`font-semibold tabular-nums ${process.netProfit >= 0 ? 'text-slate-900' : 'text-red-600'}`}>{formatCurrency(process.netProfit)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Integração dos processos com o Financeiro */}
      <FinancialCaseIntegration onRefresh={loadProcessesFinancialData} />
    </div>
  );
};
